import { Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check.svg";
import { ReactComponent as StackIcon } from "Components/_Shared/Icons/Assets/stack.svg";
import type { MovableAssetItem } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { MOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import type { FC } from "react";

type Props = {
  readonly asset: MovableAssetItem;
  readonly onEdit: () => void;
};

export const MovableAssetTile: FC<Props> = ({ asset, onEdit }) => {
  const { t } = useResource();
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      justifyContent="flex-start"
      alignItems="flex-start"
      gap={Spacing.L}
      sx={{
        border: `1px solid ${theme.colors.borderLight}`,
        borderRadius: theme.spacing(Radius.M),
        padding: theme.spacing(Spacing.L),
        boxShadow: theme.shadows[1],
        background: theme.colors.white,
        cursor: "pointer",
      }}
      onClick={onEdit}
    >
      <Stack spacing={Spacing.M} width="100%" direction="column" justifyContent="flex-start" mt={theme.spacing(Spacing.XS)}>
        <Stack spacing={Spacing.XS}>
          <Typography variant="body2" color={theme.colors.textPrimary} fontWeight={600}>
            {asset.name}
          </Typography>
          <Typography variant="caption" color={theme.colors.textSecondary} fontWeight={400}>
            {asset.note}
          </Typography>
        </Stack>

        <Grid container gap={Spacing.M}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
            gap={Spacing.S}
            sx={{
              height: "22px",
              background: theme.colors.gray,
              padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
              borderRadius: theme.spacing(Radius.XL),
            }}
          >
            <Typography variant="caption" fontWeight={500}>
              {t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType[MOVABLE_SUBTYPE_RESOURCE_KEY[asset.movableSubType]])}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
            gap={Spacing.S}
            sx={{
              height: "22px",
              background: theme.colors.gray,
              padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
              borderRadius: theme.spacing(Radius.XL),
            }}
          >
            <StackIcon />
            <Typography variant="caption" fontWeight={500}>
              {formatCurrency(asset.amount.value, asset.amount.currencyCode)}
            </Typography>
          </Box>
          {asset.isCoveredByInsurance && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={Spacing.S}
              sx={{
                height: "22px",
                background: theme.palette.success.light,
                padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                borderRadius: theme.spacing(Radius.XL),
              }}
            >
              <CheckIcon width={12} height={12} color={theme.palette.success.main} />
              <Typography variant="caption" color={theme.palette.success.main} fontWeight={500}>
                {t(Resources.AnalysisDetail.Assets.Insured)}
              </Typography>
            </Box>
          )}
          {asset.isUsableForGoals && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={Spacing.S}
              sx={{
                height: "22px",
                background: theme.palette.success.light,
                padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                borderRadius: theme.spacing(Radius.XL),
              }}
            >
              <CheckIcon width={12} height={12} color={theme.palette.success.main} />
              <Typography variant="caption" color={theme.palette.success.main} fontWeight={500}>
                {t(Resources.AnalysisDetail.Assets.Usable)}
              </Typography>
            </Box>
          )}
          {!asset.isUsableForGoals && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={Spacing.S}
              sx={{
                height: "22px",
                background: theme.palette.error.light,
                padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                borderRadius: theme.spacing(Radius.XL),
              }}
            >
              <CheckIcon width={12} height={12} color={theme.palette.error.main} />
              <Typography variant="caption" color={theme.palette.error.main} fontWeight={500}>
                {t(Resources.AnalysisDetail.Assets.Unusable)}
              </Typography>
            </Box>
          )}
        </Grid>
      </Stack>
    </Stack>
  );
};
